import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { ReadConfigService } from '../common/read-config.service';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Global } from '../../global.constants';
import { map, catchError, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AdjustmentService {

  constructor(
    private http: HttpClient,
    private readConfigService: ReadConfigService) { }

  readonly rootUrl = this.readConfigService.getApiUrl();
  apiTimeout = this.readConfigService.getApiTimeout();
  reqHeader = new HttpHeaders({ 'Content-Type': 'application/json' });

  getAdjustmentHistory(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJUSTMENT_HISTORY_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getAdjustmentRecords(id: number, userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJUSTMENT_RECORDS_ENDPOINT;
    const obj = { adjustmentId: id, userInfo };
    return this.http.post<any>(url, obj, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  submitAdjustments(data: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('userInfo', data.userInfo);
    formData.append('uploadFile', data.uploadFile.rawFile);
    formData.append('contributionWithheld', data.contributionWithheld);
    formData.append('adjustmentDetails', data.adjustmentDetails);
    formData.append('isCurrentFY', data.isCurrentFY);
    formData.append('isDebitInvoice', data.isDebitInvoice);
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    const url = this.rootUrl + Global.BASE_POST_ADJUSTMENT_RECORDS_ENDPOINT;
    return this.http.post<any>(url, formData, { headers })
      .pipe(this.handleError());
  }

  deleteAdjustment(id: number, userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_ADJUSTMENT_DELETE_ENDPOINT;
    const obj = { adjustmentId: id, userInfo };
    return this.http.post<any>(url, obj, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  // ADJUSTMENT REPORTS V2 SERVICE CALL
  getAdjUploadUrl() {
    return this.rootUrl + Global.BASE_ADJUSTMENT_FILE_UPLOAD_ENDPOINT;
  }

  getEmployeeInfo(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_EMPLOYEE_DETAILS_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getAdjustmentV2Records(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJUSTMENT_V2_RECORDS_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getAdjustmentV2Details(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJUSTMENT_V2_DETAILS_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getCorrespondingAdjDetails(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_CORRESPONDING_ADJ_V2_DETAILS_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getAdjustmentInputs(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJUSTMENT_INPUTS_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getPayrollAdjRecords(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_PAYROLL_ADJ_REPORTS_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getOwnerList(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_ADJ_OWNER_LIST_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  updateAdjustmentOwner(inputData: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_UPDATE_ADJ_OWNER_ENDPOINT;
    return this.http.post<any>(url, inputData, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  saveAdjustmentRecord(inputData: any, isNew?: boolean): Observable<any> {
    const action = isNew ? 'create' : 'update';
    if (action === 'create') {
      return this.createRecord(inputData);
    } else {
      return this.updateRecord(inputData);
    }
  }

  createRecord(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_ADJ_RECORD_CREATE_ENDPOINT;
    return this.http.post<any>(url, data)
      .pipe(this.handleError());
  }

  updateRecord(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_ADJ_RECORD_UPDATE_ENDPOINT;
    return this.http.post<any>(url, data)
      .pipe(this.handleError());
  }

  deleteAdjRecord(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_ADJ_RECORD_DELETE_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  deleteAdjReport(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_ADJ_REPORT_DELETE_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  submitV2Adjustments(inputInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_SUBMIT_ADJUSTMENT_DATA_ENDPOINT;
    return this.http.post<any>(url, inputInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  getRejectedRecords(userInfo: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_GET_REJECTED_ADJUSTMENT_ENDPOINT;
    return this.http.post<any>(url, userInfo, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  deleteRejectedRecords(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_DLT_REJECTED_ADJUSTMENT_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  moveRejectedRecords(data: any): Observable<any> {
    const url = this.rootUrl + Global.BASE_MOVE_REJECTED_ADJUSTMENT_ENDPOINT;
    return this.http.post<any>(url, data, { headers: this.reqHeader })
      .pipe(this.handleError());
  }

  handleError() {
    return timeout(this.apiTimeout),
      catchError((err: any) => {
        return throwError(err || 'Server error');
      }),
      map((v: any) => {
        if (v) {
          return v;
        }
      });
  }
}
